import React from "react";
import { Card, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faPrint,
  faCopy,
  faFileAlt,
} from "@fortawesome/free-solid-svg-icons";
import Markdown from "markdown-to-jsx";

// Custom code block component for syntax highlighting
const PreBlock = ({ children, ...rest }) => {
  if ("type" in children && children["type"] === "code") {
    const props = children["props"];
    return <pre {...rest}><code {...props}>{props.children}</code></pre>;
  }
  return <pre {...rest}>{children}</pre>;
};

const AnalysisDetails = ({ analysis, fileName, onSave, onBack }) => {
  if (!analysis) {
    return <div className="text-center text-light">No analysis data available</div>;
  }

  // Check if we have results in the analysis object
  const markdownContent = analysis.scan_results || analysis.results || "";
  // Use file_name from analysis if available, otherwise use the passed fileName
  const displayFileName = analysis.file_name || fileName || "Unnamed Contract";
  
  const handleCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>BeatAI - Contract Analysis - ${displayFileName}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              padding: 20px;
              margin: 0;
            }
            .header {
              display: flex;
              align-items: center;
              margin-bottom: 30px;
              padding-bottom: 20px;
              border-bottom: 1px solid #eaeaea;
            }
            .logo {
              height: 40px;
              margin-right: 20px;
            }
            h1, h2, h3 { color: #444; }
            h1 { font-size: 1.8em; margin-top: 5px; }
            h2 { font-size: 1.5em; }
            h3 { font-size: 1.3em; }
            pre {
              background-color: #f8f9fa;
              border: 1px solid #eaeaea;
              border-radius: 3px;
              padding: 10px;
              overflow-x: auto;
            }
            code {
              font-family: Consolas, Monaco, 'Andale Mono', monospace;
              font-size: 0.9em;
            }
            ul, ol {
              padding-left: 20px;
            }
            img {
              max-width: 100%;
            }
            .print-button {
              position: fixed;
              top: 20px;
              right: 20px;
              padding: 8px 16px;
              background-color: #6e2ae8;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-weight: bold;
            }
            @media print {
              .print-button {
                display: none !important;
              }
              @page {
                size: auto;
                margin: 20mm 10mm 20mm 10mm;
              }
              .header {
                margin-bottom: 20px;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="https://beatai.io/assets/img/beatai-logo.svg" alt="BeatAI Logo" class="logo">
            <h1>Contract Analysis Report</h1>
          </div>
          <p><strong>File:</strong> ${displayFileName}</p>
          <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
          <div class="analysis-content">
            ${document.getElementById('markdown-content').innerHTML}
          </div>
          <button class="print-button" onclick="window.print()">Print Report</button>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="analysis-details">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="text-light mb-2">Analysis Results</h4>
          <p className="text-muted">
            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
            {displayFileName}
          </p>
        </div>
        <div>
          <Button
            variant="outline-primary"
            className="dark-outline-button me-2"
            onClick={() => handleCopyToClipboard(markdownContent)}
          >
            <FontAwesomeIcon icon={faCopy} className="me-2" />
            Copy Text
          </Button>
          <Button
            variant="outline-primary"
            className="dark-outline-button me-2"
            onClick={handlePrint}
          >
            <FontAwesomeIcon icon={faPrint} className="me-2" />
            Print
          </Button>
        </div>
      </div>

      <Card className="dark-card-alt p-4">
        <div id="markdown-content" style={{ color: "#d9d9d9" }}>
          <Markdown
            options={{
              overrides: {
                pre: PreBlock,
                h3: {
                  props: {
                    style: {
                      fontSize: "1.1em",
                      color: "#d9d9d9"
                    },
                  },
                },
                h2: {
                  props: {
                    style: {
                      fontSize: "1.2em",
                      color: "#d9d9d9"
                    },
                  },
                },
                h1: {
                  props: {
                    style: {
                      fontSize: "1.3em",
                      color: "#d9d9d9"
                    },
                  },
                },
                p: {
                  props: {
                    style: {
                      color: "#d9d9d9"
                    }
                  }
                },
                ul: {
                  props: {
                    style: {
                      color: "#d9d9d9"
                    }
                  }
                },
                li: {
                  props: {
                    style: {
                      color: "#d9d9d9"
                    }
                  }
                },
                table: {
                  props: {
                    style: {
                      width: "100%",
                      borderCollapse: "collapse",
                      color: "#d9d9d9",
                      marginBottom: "1rem"
                    }
                  }
                },
                thead: {
                  props: {
                    style: {
                      backgroundColor: "#2c2c2c"
                    }
                  }
                },
                th: {
                  props: {
                    style: {
                      padding: "0.75rem",
                      color: "#ffffff",
                      borderBottom: "2px solid #2c2c2c",
                      textAlign: "left"
                    }
                  }
                },
                td: {
                  props: {
                    style: {
                      padding: "0.75rem",
                      borderBottom: "1px solid #2c2c2c",
                      color: "#d9d9d9"
                    }
                  }
                }
              },
            }}
          >
            {markdownContent}
          </Markdown>
        </div>
      </Card>
    </div>
  );
};

export default AnalysisDetails;